// MessageList — admin view of all messages, with filter chips and
// retract action.
//
// "Retract" is a soft delete (sets retracted=true). Retracted rows
// stay greyed in the list (audit trail) but learners no longer see
// them. A silent FCM push (notifyMessageRetracted) tells learner SWs
// to purge the message from IDB cache.

import {
  Hairline,
  Han,
  HUD,
  MonoData,
  MonoLabel,
  T,
  TextBtn,
} from "@goodbye/ui";
import { messageDoc } from "@goodbye/firebase";
import {
  Timestamp,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminSyncCtx } from "../context";

// Stage 6: 「全部」+「未撤回」merged into a single「已發出」chip,
// mutually exclusive with「已撤回」. Together they cover everything.
type ReleaseFilter = "sent" | "retracted";

function formatTs(ts: Timestamp | null | undefined): string {
  if (!ts || typeof ts.toDate !== "function") return "—";
  const d = ts.toDate();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${m}/${day} ${hh}:${mm}`;
}

export function MessageList() {
  const navigate = useNavigate();
  const { messages, users, loading } = useAdminSyncCtx();
  const [releaseFilter, setReleaseFilter] = useState<ReleaseFilter>("sent");
  const [busy, setBusy] = useState<Set<string>>(new Set());
  // 撤回二段確認:第一次點 arm(顯示「· 確 定 撤 回?」),第二次才真的
  // 撤回。4s auto-revert,避免一顆 arm 態一直亮。2026-05-30。
  const [confirmingRetract, setConfirmingRetract] = useState<string | null>(null);
  useEffect(() => {
    if (!confirmingRetract) return;
    const t = window.setTimeout(() => setConfirmingRetract(null), 4000);
    return () => window.clearTimeout(t);
  }, [confirmingRetract]);

  // userId → 姓名 lookup so audience can show real names, not just counts.
  const nameByUserId = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) m.set(u.id, u.name);
    return m;
  }, [users]);

  const visible = useMemo(() => {
    return messages.filter((m) => {
      if (releaseFilter === "sent") return m.retracted !== true;
      return m.retracted === true;
    });
  }, [messages, releaseFilter]);

  function markBusy(id: string) {
    setBusy((prev) => new Set(prev).add(id));
  }
  function clearBusy(id: string) {
    setBusy((prev) => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
  }

  async function retract(id: string) {
    if (busy.has(id)) return;
    markBusy(id);
    try {
      await updateDoc(messageDoc(id), {
        retracted: true,
        retractedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("[MessageList] retract failed", err);
    } finally {
      clearBusy(id);
    }
  }

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        background: T.paper,
        paddingBottom: "calc(140px + env(safe-area-inset-bottom, 0px))",
      }}
    >
      <HUD label="工 作 端" />
      <div
        style={{
          padding: "20px 32px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
          }}
        >
          <Han size={22} color={T.ink900}>訊 息</Han>
          <TextBtn
            size={13}
            color={T.ink900}
            ls="0.12em"
            onClick={() => navigate("/messages/new")}
            style={{
              padding: "8px 14px",
              border: `1px solid ${T.ink200}`,
              whiteSpace: "nowrap",
            }}
          >
            新 建
          </TextBtn>
        </div>
        <MonoLabel size={11} color={T.ink500}>
          {loading
            ? "讀取中…"
            : `共 ${messages.length} 則 · 篩選後 ${visible.length} 則`}
        </MonoLabel>
      </div>
      <Hairline style={{ margin: "8px 32px 0" }} />

      <div
        style={{
          padding: "10px 32px",
          display: "flex",
          gap: 14,
          alignItems: "baseline",
          flexWrap: "wrap",
        }}
      >
        {(
          [
            ["sent", "已 發 出"],
            ["retracted", "已 撤 回"],
          ] as const
        ).map(([v, zh]) => {
          const active = v === releaseFilter;
          return (
            <button
              type="button"
              key={v}
              onClick={() => setReleaseFilter(v)}
              style={{
                fontFamily: T.han,
                fontSize: 12,
                letterSpacing: "0.18em",
                color: active ? T.ink900 : T.ink350,
                background: "transparent",
                border: "none",
                borderBottom: `1px solid ${active ? T.ink900 : "transparent"}`,
                padding: "2px 0",
                cursor: "pointer",
              }}
            >
              {zh}
            </button>
          );
        })}
      </div>
      <Hairline style={{ margin: "0 32px" }} />

      <div
        style={{
          padding: "12px 32px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {visible.length === 0 && !loading && (
          <Han size={14} color={T.ink500} lh={1.9}>
            還沒有訊息。點「· 新 建」開始。
          </Han>
        )}
        {visible.map((m) => {
          const isBusy = busy.has(m.id);
          return (
            <div
              key={m.id}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                paddingBottom: 14,
                borderBottom: `1px solid ${T.ink100}`,
                opacity: m.retracted ? 0.4 : 1,
              }}
            >
              {/* Stage 6: title removed. Row 1 = text (2-line ellipsis)
                  + timestamp (top-right). Admin keeps the 2-line preview
                  for scan-ability across many messages. */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 12,
                }}
              >
                {m.text ? (
                  <Han
                    size={15}
                    color={T.ink900}
                    lh={1.55}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {m.text}
                  </Han>
                ) : (
                  <Han size={15} color={T.ink350} style={{ flex: 1 }}>
                    (空 白)
                  </Han>
                )}
                <MonoData size={12} color={T.ink500} style={{ flexShrink: 0 }}>
                  {formatTs(m.createdAt)}
                </MonoData>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 14,
                  alignItems: "baseline",
                  flexWrap: "wrap",
                }}
              >
                <MonoLabel size={11} color={T.ink500}>
                  {m.broadcast
                    ? "全 體"
                    : m.audienceUserIds.length === 0
                      ? "(無收件人)"
                      : `${m.audienceUserIds.length} 人 · ${m.audienceUserIds
                          .map((uid) => nameByUserId.get(uid) ?? uid)
                          .join(" · ")}`}
                </MonoLabel>
                {m.photoUrl && (
                  <MonoLabel size={11} color={T.ink700}>
                    · 附 圖
                  </MonoLabel>
                )}
                {m.retracted && (
                  <MonoLabel size={11} color={T.ink350}>
                    已 撤 回
                  </MonoLabel>
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  marginTop: 4,
                  justifyContent: "flex-end",
                }}
              >
                {m.retracted ? (
                  <TextBtn
                    size={12}
                    color={T.ink900}
                    ls="0.12em"
                    framed
                    onClick={() => navigate(`/messages/new?from=${m.id}`)}
                    style={{
                      padding: "8px 14px",
                      border: `1px solid ${T.ink200}`,
                      whiteSpace: "nowrap",
                    }}
                  >
                    再 次 送 出
                  </TextBtn>
                ) : (
                  <TextBtn
                    size={12}
                    color={confirmingRetract === m.id ? T.ink900 : T.ink500}
                    ls="0.12em"
                    framed
                    onClick={() => {
                      if (isBusy) return;
                      if (confirmingRetract === m.id) {
                        setConfirmingRetract(null);
                        retract(m.id);
                      } else {
                        setConfirmingRetract(m.id);
                      }
                    }}
                    disabled={isBusy}
                    style={{
                      padding: "8px 12px",
                      border: `1px solid ${
                        confirmingRetract === m.id ? T.ink900 : T.ink200
                      }`,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isBusy
                      ? "處 理 中…"
                      : confirmingRetract === m.id
                        ? "確 定 撤 回?"
                        : "撤 回"}
                  </TextBtn>
                )}
              </div>
            </div>
          );
        })}
      </div>

    </div>
  );
}
